/**
 * deep-link.js — la reserva que llega YA EMPEZADA desde fuera del sitio.
 *
 * ── QUÉ AGUJERO TAPA ──────────────────────────────────────────────────────────
 * El SEO del builder publica en el JSON-LD de cada web de hostelería un
 * `potentialAction: ReserveAction` cuyo `urlTemplate` es
 * `<pagina-de-reserva>?d={date}&p={party_size}&z={zone}`
 * (`AIBuilderGenerator/views.py::_seo_reserve_action`). O sea: le prometemos a Google —y a
 * cualquier agente que lea la ficha— que esa URL abre la reserva con el día, el grupo y la sala ya
 * puestos. Nadie leía esos parámetros: el visitante que llegaba desde el panel de Google aterrizaba
 * en el paso 1 con los valores por defecto y tenía que volver a teclear lo que ya había dicho.
 *
 * ── POR QUÉ UN MÓDULO APARTE Y PURO ───────────────────────────────────────────
 * Sin `window`, sin DOM y sin importar nada: se prueba en node (tests/_deep_link.mjs) igual que
 * `cta-destino.js`, y ahí es donde se guarda la PARIDAD con el `urlTemplate` que emite Python — que
 * es el contrato que de verdad se puede desincronizar, porque vive en otro lenguaje.
 *
 * ── LO QUE ESTE MÓDULO **NO** DECIDE ──────────────────────────────────────────
 * Aquí solo se valida la FORMA y la pertenencia (¿es una fecha?, ¿ese grupo cabe en el rango de la
 * política?, ¿esa sala existe en el bloque?). Si el día está cerrado, pasado o fuera de antelación
 * lo dice `diaBloqueado` en el runtime, que ya es el espejo de las reglas del motor; duplicarlo
 * aquí sería una segunda verdad. Y la última palabra la tiene SIEMPRE el servidor: un deep-link es
 * una sugerencia de arranque, no una reserva.
 */

/**
 * Nombres de los parámetros. SSOT del lado JS; `tests/test_deep_link.py` comprueba que coinciden
 * con los del `urlTemplate` que escribe `_seo_reserve_action` — si alguien renombra uno de los dos,
 * salta el test en vez de romperse en silencio el enlace que ve Google.
 */
export const DEEP_LINK_PARAMS = { fecha: 'd', personas: 'p', zona: 'z', hora: 'h' };

const RE_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_HORA = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** 'HH:MM' normalizado, o '' si no lo es. */
export function horaValida(crudo) {
    const m = RE_HORA.exec(String(crudo || '').trim());
    return m ? `${String(m[1]).padStart(2, '0')}:${m[2]}` : '';
}

/**
 * 'YYYY-MM-DD' (+ hora si venía pegada), o null.
 *
 * Acepta el ISO COMPLETO (`2026-08-12T21:00`) además de la fecha suelta porque `{date}` de un
 * `ReserveAction` de restaurante se rellena a menudo con fecha Y hora: quedarnos solo con el día
 * tiraría justo el dato más valioso del enlace. Se comprueba que la fecha EXISTE de verdad
 * (`2026-02-31` no) reconstruyéndola, no solo que encaje con el patrón.
 */
export function fechaValida(crudo) {
    const txt = String(crudo || '').trim();
    if (!txt) return null;
    const [dia, resto] = txt.split(/[T ]/);
    const m = RE_FECHA.exec(dia || '');
    if (!m) return null;
    const y = Number(m[1]); const mes = Number(m[2]); const d = Number(m[3]);
    const f = new Date(y, mes - 1, d);
    if (f.getFullYear() !== y || f.getMonth() !== mes - 1 || f.getDate() !== d) return null;
    return { fecha: dia, hora: resto ? horaValida(resto.slice(0, 5)) : '' };
}

/**
 * Lee los parámetros de arranque de una query string.
 *
 * @param {string} busqueda  `location.search` (con o sin '?').
 * @param {object} opciones  `{ paxMin, paxMax, zonas: ['salon', …] }`. Los tres son opcionales: sin
 *   rango de pax no se acota el grupo, y sin lista de salas no se acepta ninguna (una sala que el
 *   bloque no conoce no se puede seleccionar, así que aceptarla sería pintar un chip fantasma).
 * @returns {object} solo las claves VÁLIDAS presentes: `{personas?, fecha?, hora?, zona?}`. Objeto
 *   vacío = no hay deep-link, que es el caso normal y el que no debe cambiar nada.
 */
export function leerDeepLink(busqueda, opciones) {
    const salida = {};
    const txt = String(busqueda || '').replace(/^\?/, '');
    if (!txt) return salida;
    const o = opciones || {};

    let params;
    try {
        params = new URLSearchParams(txt);
    } catch (_) {
        return salida;                       // query ilegible: se ignora entera, nunca se lanza
    }

    const fecha = fechaValida(params.get(DEEP_LINK_PARAMS.fecha));
    if (fecha) {
        salida.fecha = fecha.fecha;
        if (fecha.hora) salida.hora = fecha.hora;      // venía pegada al ISO
    }

    // La hora SUELTA manda sobre la que viniera dentro del ISO: es la más explícita de las dos.
    const hora = horaValida(params.get(DEEP_LINK_PARAMS.hora));
    if (hora) salida.hora = hora;

    const pax = parseInt(String(params.get(DEEP_LINK_PARAMS.personas) || '').trim(), 10);
    if (Number.isFinite(pax) && pax > 0) {
        const min = Number(o.paxMin) || 1;
        const max = Number(o.paxMax) || 0;
        // Fuera de rango se DESCARTA en vez de recortarse a los topes: un enlace que pide mesa para
        // 20 en un local que llega a 10 no quiere decir "mesa para 10" — recortarlo en silencio
        // sentaría al grupo equivocado. Sin `p` válido, el widget arranca en su valor por defecto y
        // el visitante elige, que es lo que hacía antes de existir el deep-link.
        if (pax >= min && (!max || pax <= max)) salida.personas = pax;
    }

    const zona = String(params.get(DEEP_LINK_PARAMS.zona) || '').trim();
    if (zona) {
        const conocidas = Array.isArray(o.zonas) ? o.zonas.map(String) : [];
        if (conocidas.indexOf(zona) !== -1) salida.zona = zona;
    }

    return salida;
}
